import { Popover } from "@base-ui-components/react";
import clsx from "clsx";
import { useCallback } from "react";
import { FlexAligner, type FlexAlignerProps } from "src/components/FlexAligner";
import type { Merge } from "src/models/utility";
import { useDropdownContext } from "../root/Dropdown.root";
import styling from "./Dropdown.content.module.scss";

export namespace DropdownContent {
	export type ElementName = "div";
	export type ElementType = HTMLDivElement;
	export type Props = Merge<
		Omit<FlexAlignerProps, "alignment" | "id" | "direction" | "ref" | "width">,
		{
			padded?: boolean;
			clamped?: boolean;
			sideOffset?: number;
			align?: "start" | "center" | "end";
			side?: "top" | "bottom" | "left" | "right";
		}
	>;
}

export const DropdownContent = (props: DropdownContent.Props) => {
	const {
		className,
		children,
		padded = true,
		clamped = true,
		sideOffset = 4,
		align = "start",
		side = "bottom",
		spacing,
		...rest
	} = props;

	const { open, closeMenu, closeOnInteractInside } = useDropdownContext();

	const onAnyInteract = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			if (!closeOnInteractInside) return;
			const target = e.target as HTMLElement | null;
			const shouldClose = !target?.closest("[data-dropdown-close='false']");

			if (shouldClose) {
				setTimeout(() => closeMenu(), 0);
			}
		},
		[closeOnInteractInside, closeMenu],
	);

	if (!open) return null;

	return (
		<Popover.Portal>
			<Popover.Positioner sideOffset={sideOffset} align={align} side={side}>
				<Popover.Popup
					render={(popupProps) => {
						const { key, ...popupRest } = popupProps as typeof popupProps & { key?: React.Key };
						return (
							<FlexAligner
								key={key}
								{...popupRest}
								className={clsx(styling.view, {
									[styling["view--padded"]]: padded,
									[styling["view--open"]]: open,
									[styling["view--clamped"]]: clamped,
									[`${className}`]: className,
								})}
								role="menu"
								onClick={(e) => {
									rest.onClick?.(e);
									if (!e?.defaultPrevented) onAnyInteract(e);
								}}
								onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
									rest.onKeyDown?.(e);
									if (!e?.defaultPrevented && (e.key === "Enter" || e.key === " ")) {
										onAnyInteract(e);
									}
								}}
								direction="column"
								spacing={spacing}
								{...rest}
							>
								{children}
							</FlexAligner>
						);
					}}
				/>
			</Popover.Positioner>
		</Popover.Portal>
	);
};
